// src/dashboard/pages/teacher/Techniques/components/AddTechniqueDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaBookOpen, FaImage, FaPlus, FaSave, FaTimes } from "react-icons/fa";

import {
  DEFAULT_TECHNIQUE_IMAGE,
  getTechniquePhotoSrc,
  normalizeFamily,
} from "../utils/techniquesDashboardUtils";

const initialForm = {
  nom: "",
  famille: "",
  ceinture: "",
  image1_url: "",
  video_url: "",
  traduction: "",
  information: "",
  uv2: "",
};

export default function AddTechniqueDrawer({
  isOpen,
  onClose,
  onSubmit,
  saving,
  familyOptions = [],
  beltOptions = [],
}) {
  const [formData, setFormData] = useState(initialForm);
  const [familyMode, setFamilyMode] = useState("existing");
  const [newFamily, setNewFamily] = useState("");
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(initialForm);
    setFamilyMode("existing");
    setNewFamily("");
    setImgError(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [isOpen, saving, onClose]);

  const canSubmit = useMemo(() => {
    return String(formData.nom || "").trim() && !saving;
  }, [formData.nom, saving]);

  const previewSrc = useMemo(() => {
    if (imgError) return DEFAULT_TECHNIQUE_IMAGE;
    return getTechniquePhotoSrc(formData.image1_url);
  }, [formData.image1_url, imgError]);

  const updateField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "image1_url") {
      setImgError(false);
    }
  };

  const handleExistingFamilyChange = (value) => {
    setFamilyMode("existing");
    setNewFamily("");
    updateField("famille", normalizeFamily(value));
  };

  const handleNewFamilyChange = (value) => {
    const family = normalizeFamily(value);

    setFamilyMode("new");
    setNewFamily(family);
    updateField("famille", family);
  };

  const toggleFamilyMode = () => {
    if (familyMode === "existing") {
      setFamilyMode("new");
      setNewFamily("");
      updateField("famille", "");
    } else {
      setFamilyMode("existing");
      setNewFamily("");
      updateField("famille", "");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    await onSubmit?.({
      ...formData,
      famille: familyMode === "new" ? newFamily : formData.famille,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-4xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Nouvelle technique
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Ajouter une technique
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Renseignez le nom, la famille, la ceinture et les contenus
              pédagogiques associés.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto p-5"
        >
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[300px,1fr]">
            <div className="space-y-4">
              <div className="relative flex h-64 items-center justify-center overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60">
                <img
                  src={previewSrc}
                  alt="Aperçu technique"
                  onError={() => setImgError(true)}
                  className="h-full w-full object-contain"
                />

                <span className="absolute bottom-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-900">
                  <FaImage />
                </span>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  URL de l’image
                </label>

                <input
                  type="text"
                  value={formData.image1_url}
                  onChange={(event) =>
                    updateField("image1_url", event.target.value)
                  }
                  placeholder="https://... ou /uploads/..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />

                <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Après création, vous pourrez téléverser une image optimisée
                  depuis la fenêtre d’édition.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[#013E8E] shadow-sm dark:bg-slate-900 dark:text-indigo-300">
                    <FaBookOpen />
                  </span>

                  <div>
                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                      Informations pédagogiques
                    </p>
                    <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                      Seul le nom de la technique est obligatoire.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Nom *
                </label>

                <input
                  type="text"
                  value={formData.nom}
                  onChange={(event) => updateField("nom", event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  required
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Famille existante
                  </label>

                  <select
                    value={familyMode === "existing" ? formData.famille : ""}
                    onChange={(event) =>
                      handleExistingFamilyChange(event.target.value)
                    }
                    disabled={familyMode === "new"}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  >
                    <option value="">— Aucune —</option>
                    {familyOptions.map((family) => (
                      <option key={family} value={family}>
                        {family}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Nouvelle famille
                  </label>

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newFamily}
                      onChange={(event) =>
                        handleNewFamilyChange(event.target.value)
                      }
                      disabled={familyMode === "existing"}
                      placeholder="Créer une famille"
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    />

                    <button
                      type="button"
                      onClick={toggleFamilyMode}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <FaPlus />
                      {familyMode === "existing" ? "Créer" : "Liste"}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Ceinture
                </label>

                <select
                  value={formData.ceinture}
                  onChange={(event) =>
                    updateField("ceinture", event.target.value)
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                >
                  <option value="">— Aucune —</option>
                  {beltOptions.map((belt) => (
                    <option key={`${belt.id}-${belt.label}`} value={belt.label}>
                      {belt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  URL vidéo
                </label>

                <input
                  type="url"
                  value={formData.video_url}
                  onChange={(event) =>
                    updateField("video_url", event.target.value)
                  }
                  placeholder="https://..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Traduction
                </label>

                <input
                  type="text"
                  value={formData.traduction}
                  onChange={(event) =>
                    updateField("traduction", event.target.value)
                  }
                  placeholder="Ex : Nom japonais, traduction..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Informations
                </label>

                <textarea
                  value={formData.information}
                  onChange={(event) =>
                    updateField("information", event.target.value)
                  }
                  rows={4}
                  placeholder="Détails, repères pédagogiques, variantes..."
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  UV2
                </label>

                <textarea
                  value={formData.uv2}
                  onChange={(event) => updateField("uv2", event.target.value)}
                  rows={4}
                  placeholder="Contenu pédagogique / points clés UV2..."
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </div>
            </div>
          </div>
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}